import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useSelection } from '../context/SelectionContext';

/**
 * Generic hook to fetch counts per child
 * @param {Array} children - Array of children to fetch counts for
 * @param {string} endpoint - API endpoint pattern (use {childId} as placeholder)
 * @param {string} countField - The field name in the response containing the count
 * @param {string} versionKey - The context version key to listen to for refetch
 */
export function useChildrenCounts(children, endpoint, countField, versionKey) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const context = useSelection();
  const version = context[versionKey];

  const fetchCounts = useCallback(async () => {
    if (!children || children.length === 0) {
      setCounts({});
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        children.map(async (child) => {
          try {
            const url = endpoint.replace('{childId}', child.id);
            const response = await api.get(url);
            return { id: child.id, count: response.data[countField] || 0 };
          } catch {
            return { id: child.id, count: 0 };
          }
        })
      );

      const countsMap = {};
      results.forEach(({ id, count }) => {
        countsMap[id] = count;
      });
      setCounts(countsMap);
    } catch (err) {
      console.error(`Failed to fetch children counts from ${endpoint}:`, err);
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, [children, endpoint, countField]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, version]);

  return { counts, loading, refetch: fetchCounts };
}

/**
 * Generic hook to fetch total count across all children
 * @param {string} endpoint - API endpoint pattern (use {childId} as placeholder)
 * @param {string} countField - The field name in the response containing the count
 * @param {string} versionKey - The context version key to listen to for refetch
 */
export function useTotalCount(endpoint, countField, versionKey) {
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const context = useSelection();
  const version = context[versionKey];

  const fetchTotalCount = useCallback(async () => {
    setLoading(true);
    try {
      const childrenResponse = await api.get('/parent/children');
      const children = childrenResponse.data.children || [];

      if (children.length === 0) {
        setTotalCount(0);
        return;
      }

      const counts = await Promise.all(
        children.map(async (child) => {
          try {
            const url = endpoint.replace('{childId}', child.id);
            const response = await api.get(url);
            return response.data[countField] || 0;
          } catch {
            return 0;
          }
        })
      );

      setTotalCount(counts.reduce((sum, count) => sum + count, 0));
    } catch (err) {
      console.error(`Failed to fetch total count from ${endpoint}:`, err);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [endpoint, countField]);

  useEffect(() => {
    fetchTotalCount();
  }, [fetchTotalCount, version]);

  return { totalCount, loading, refetch: fetchTotalCount };
}

// Convenience hooks for specific use cases

export function useChildrenIncompleteCounts(children) {
  return useChildrenCounts(
    children,
    '/parent/children/{childId}/competition-details/incomplete-count',
    'incomplete_count',
    'incompleteCountVersion'
  );
}

export function useChildrenUnviewedCounts(children) {
  return useChildrenCounts(
    children,
    '/parent/children/{childId}/unviewed-suggested-courses',
    'unviewed_count',
    'unviewedCountVersion'
  );
}

export function useTotalIncompleteCount() {
  return useTotalCount(
    '/parent/children/{childId}/competition-details/incomplete-count',
    'incomplete_count',
    'incompleteCountVersion'
  );
}

export function useTotalUnviewedCount() {
  return useTotalCount(
    '/parent/children/{childId}/unviewed-suggested-courses',
    'unviewed_count',
    'unviewedCountVersion'
  );
}
